import React, { FC } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";

import {
    fetchCompensationProcedures,
    fetchCompensationProcedureColumns,
    setSelectedIds as setSelectedIdsAction,
    setSearchString as setSearchStringAction,
    setPageNumber as setPageNumberAction,
    setLimitNumber as setLimitNumberAction,
    setSortData as setSortDataAction,
    setFilterData as setFilterDataAction,
    setError as setErrorAction,
} from "../../../../../reducers/admin/compensationProceduresSlice";

import useFilters from "../../../../common/hooks/useFilters";
import useReduxTable from "../../../../common/hooks/useReduxTable";
import CommonTable from "../../../../common/table";
import { ProgressDialog, ErrorsDialog } from "../../../../common/dialogs";
import { EmptyTabContent } from "../../../../common/empty-tab-content";
import { RootState } from "../../../../../store";
import { Column, TableFilterData, TableSortData } from "types/common/table/table-types";
import { CompensationProcedure } from "@/types/compensation-procedures";
import { ResponseError } from "types/common/responses/responses-types";

const getCellValue = (row, column) => {
    switch (column.id) {
        case "id":
            return row.id;

        default:
            return row[column.id];
    }
};

type EmptyCompensationProceduresPageProps = {
    handleAddProcedureClick: () => void;
};
const EmptyCompensationProceduresPage: FC<EmptyCompensationProceduresPageProps> = ({ handleAddProcedureClick }) => {
    const intl = useIntl();

    const text = intl.formatMessage({ id: "empty-compensation-procedures-page.text" });
    const buttonText = intl.formatMessage({ id: "compensation-procedures-list-page.toolbar.btn-create-compensation-procedure" });

    return <EmptyTabContent text={text} buttonText={buttonText} onButtonClick={handleAddProcedureClick} />;
};

const CompensationProceduresListPage = () => {
    const intl = useIntl();
    const navigate = useNavigate();

    const {
        rows,
        columns,
        total,
        busy,
        selectedIds,
        searchString,
        setSearchString,
        pageNumber,
        setPageNumber,
        limitNumber,
        setLimitNumber,
        filterData,
        setFilterData,
        sortData,
        setSortData,
        error,
        setError,
    } = useReduxTable(
        (state: RootState) => state.admin.compensationProcedures.compensationProcedures,
        fetchCompensationProcedures,
        (state: RootState) => state.admin.compensationProcedures.columns,
        fetchCompensationProcedureColumns,
        (state: RootState) => state.admin.compensationProcedures.selectedIds,
        setSelectedIdsAction,
        (state: RootState) => state.admin.compensationProcedures.searchString,
        setSearchStringAction,
        (state: RootState) => state.admin.compensationProcedures.pageNumber,
        setPageNumberAction,
        (state: RootState) => state.admin.compensationProcedures.limitNumber,
        setLimitNumberAction,
        (state: RootState) => state.admin.compensationProcedures.sortData,
        setSortDataAction,
        (state: RootState) => state.admin.compensationProcedures.filterData,
        setFilterDataAction,
        (state: RootState) => state.admin.compensationProcedures.error,
        setErrorAction,
        (state: RootState) => state.admin.compensationProcedures.total,
        (state: RootState) => state.admin.compensationProcedures.busy
    );

    const busyType = useSelector((state: RootState) => state.admin.compensationProcedures.busyType);

    const getFilters = useFilters("compensationProcedures");

    const handleAddProcedureClick = () => {
        navigate("form");
    };

    const handleRowClick = (compensationProcedure) => {
        navigate(`form/${compensationProcedure.id}`);
    };

    const getTableRowId = (row) => row.id;

    const toolbarActions = [
        {
            id: "title",
            title: intl.formatMessage({ id: "compensation-procedures-list-page.toolbar.text.title" }),
            type: "text",
            position: "begin",
        },
        {
            id: "add",
            type: "button",
            position: "end",
            title: intl.formatMessage({ id: "compensation-procedures-list-page.toolbar.btn-create-compensation-procedure" }),
            callback: () => {
                handleAddProcedureClick();
            },
        },
    ];
    const typedError = error as ResponseError | null;
    const typedFilterData = filterData as TableFilterData[];

    if (total === 0 && !searchString && typedFilterData.length === 0 && !busy) {
        return <EmptyCompensationProceduresPage handleAddProcedureClick={handleAddProcedureClick} />;
    }

    return (
        <>
            <CommonTable
                rows={rows as CompensationProcedure[]}
                columns={columns as Column[]}
                filterData={filterData as TableFilterData[]}
                setFilterData={setFilterData}
                sortData={sortData as TableSortData[]}
                setSortData={setSortData as (value: TableSortData) => void}
                toolbarActions={toolbarActions}
                selectedIds={selectedIds as string[]}
                getRowId={getTableRowId}
                pageNumber={pageNumber as number}
                setPageNumber={setPageNumber as (value: number) => void}
                limitNumber={limitNumber as number}
                setLimitNumber={setLimitNumber as (value: number) => void}
                total={total as number}
                onRowClick={handleRowClick}
                searchString={searchString as string}
                setSearchString={setSearchString as (value: string) => void}
                getCellValue={getCellValue}
                getFilters={getFilters}
                useKeyboardNavigation={false}
                toolbar={[]}
            />

            <ProgressDialog title={""} progress={""} open={busy} busyType={busyType} />

            <ErrorsDialog error={typedError} open={typedError?.type === "error"} onClose={() => (setError as (value: string | null) => void)(null)} />
        </>
    );
};

export default CompensationProceduresListPage;
